import { Component, ErrorInfo, ReactNode } from "react";
import axios from "axios";

import i18n from "../i18n";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  /** Экран не собрался, потому что не загрузился его чанк, а не из-за кода. */
  isChunkError: boolean;
}

/**
 * Не докачавшийся чанк — отдельный случай.
 *
 * Экраны грузятся лениво, каждый своим файлом. Если файла на сервере нет
 * (выложили новую сборку, а чанки остались старые) или оборвалась сеть,
 * webpack бросает ChunkLoadError. Апач на отсутствующий файл отдаёт index.html,
 * поэтому в тексте ошибки может оказаться и синтаксическая жалоба на "<".
 */
function isChunkLoadError(error: Error): boolean {
  const message = `${error?.name ?? ""} ${error?.message ?? ""}`;
  return (
    error?.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Unexpected token '<'/i.test(message)
  );
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    sendErrorLogToServer({
      message: error.toString(),
      stack: errorInfo.componentStack ?? "Stack trace not available",
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="loader-container error-boundary">
          <h1>
            {this.state.isChunkError
              ? i18n.t("Failed to load this screen.")
              : i18n.t("Something went wrong.")}
          </h1>
          {/* Без этой кнопки экран был тупиком: в мини-аппе перезагрузить
              страницу нечем — адресной строки нет. */}
          <button
            type="button"
            className="error-boundary-reload"
            onClick={() => window.location.reload()}
          >
            {i18n.t("Reload")}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

interface ErrorLogData {
  message: string;
  stack: string;
}

function sendErrorLogToServer(errorData: ErrorLogData) {
  axios
    .post("/api/logs/log_js_error.php", errorData)
    .catch((error) => console.error("Error sending log:", error));
}

export default ErrorBoundary;
